import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { AlertTriangle, KeyRound, ShieldHalf, Terminal } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button } from '../components/ui';
import { useAuthStore } from '../store/useAuthStore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/dev-login')({
  component: DevLogin,
});

/**
 * Dev-only sign-in — paste a Cognito ID token JWT to drive the frontend
 * against the live API while the proper Hosted UI flow gets wired.
 *
 * Clean professional SaaS panel: a centered card on bg-void with the Forge brand
 * mark + a "dev" badge, a labelled token field, and a clean CLI-helper card. The
 * JWT decode / signIn / redirect logic is unchanged.
 *
 * Mint a token via the AWS CLI:
 *   JWT=$(aws cognito-idp admin-initiate-auth \
 *     --user-pool-id eu-central-1_yHzoFRd6Y \
 *     --client-id 4vm99luo2k1s9qdvkq9ot4035k \
 *     --auth-flow ADMIN_USER_PASSWORD_AUTH \
 *     --auth-parameters USERNAME=smoke-test@cyberelites.ae,PASSWORD='Smoke!Test#2026' \
 *     --profile forge --region eu-central-1 \
 *     --query 'AuthenticationResult.IdToken' --output text)
 *   echo $JWT
 */
function DevLogin() {
  const signIn = useAuthStore((s) => s.signIn);
  const [jwt, setJwt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    setError(null);
    const trimmed = jwt.trim();
    if (!trimmed) {
      setError('Paste a JWT first.');
      return;
    }
    const parts = trimmed.split('.');
    if (parts.length !== 3) {
      setError('That does not look like a JWT (expected 3 dot-separated segments).');
      return;
    }
    try {
      const claimsSegment = parts[1] ?? '';
      const payload = JSON.parse(atob(claimsSegment.replace(/-/g, '+').replace(/_/g, '/')));
      const sub = payload.sub as string | undefined;
      const email = (payload.email as string | undefined) ?? sub ?? 'dev-user';
      const tenantId = String(payload['custom:tenant_id'] ?? '');
      const role = (payload['custom:role'] as string | undefined) ?? 'trainee';
      if (!sub || !tenantId) {
        setError('JWT missing sub / custom:tenant_id claim. Was this issued by the Forge user pool?');
        return;
      }
      const expRaw = (payload as { exp?: unknown }).exp;
      const expiresAt =
        typeof expRaw === 'number' && Number.isFinite(expRaw)
          ? expRaw * 1000
          : Date.now() + 3600 * 1000;
      signIn(
        { userId: sub, email, tenantId, role: role as 'trainee' | 'instructor' | 'org_admin' | 'superadmin' },
        { idToken: trimmed, accessToken: trimmed, refreshToken: '', expiresAt },
      );
      window.location.href = '/';
    } catch (e) {
      setError(`Failed to decode JWT: ${(e as Error).message}`);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-void px-5 py-10">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/3 size-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-neon/5 blur-[140px]"
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-[580px] space-y-4"
      >
        {/* Brand mark */}
        <div className="flex flex-col items-center text-center">
          <span className="flex size-12 items-center justify-center rounded-2xl bg-neon text-white shadow-sm">
            <ShieldHalf className="size-6" />
          </span>
          <div className="mt-4 flex items-center gap-2">
            <h1 className="font-display text-[22px] font-bold tracking-tight text-txt-bright">
              Developer sign-in
            </h1>
            <Badge variant="warn">Dev</Badge>
          </div>
          <p className="mt-1.5 max-w-md text-[13.5px] leading-relaxed text-txt-2">
            Cognito Hosted UI is not yet wired. Paste a Cognito ID token to drive the frontend
            against the live backend.
          </p>
        </div>

        {/* Token card */}
        <div className="hud-panel p-6">
          <p className="text-[13px] leading-relaxed text-txt-2">
            The token is persisted to{' '}
            <code className="rounded bg-panel px-1.5 py-0.5 font-mono text-[12px] text-txt">
              localStorage
            </code>{' '}
            and signs you in against{' '}
            <code className="rounded bg-panel px-1.5 py-0.5 font-mono text-[12px] text-txt">
              {import.meta.env.VITE_FORGE_API_URL ?? '(mock)'}
            </code>
            . Sign out clears it.
          </p>

          <label className="mt-5 block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">ID token (JWT)</span>
            <textarea
              value={jwt}
              onChange={(e) => setJwt(e.currentTarget.value)}
              placeholder="eyJraWQ…"
              spellCheck={false}
              rows={5}
              className="w-full resize-y rounded-lg border border-bd bg-input-bg px-3 py-2.5 font-mono text-[12px] text-txt outline-none transition-colors duration-150 placeholder:text-txt-muted focus:border-neon/50 focus:ring-2 focus:ring-neon/15"
            />
          </label>

          {error ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-hud-red/40 bg-hud-red/10 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-hud-red" />
              <span className="text-[12.5px] leading-relaxed text-hud-red">{error}</span>
            </div>
          ) : null}

          <div className="mt-5">
            <Button leftIcon={<KeyRound className="size-4" />} onClick={handleSubmit}>
              Sign in
            </Button>
          </div>
        </div>

        {/* CLI helper card */}
        <div className="hud-panel p-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-lg bg-panel text-txt-2">
              <Terminal className="size-4" />
            </span>
            <span className="text-[13px] font-semibold text-txt-bright">Mint a token via CLI</span>
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-bd bg-deep p-4 font-mono text-[11px] leading-relaxed text-txt-2">
{`aws cognito-idp admin-initiate-auth \\
  --user-pool-id eu-central-1_yHzoFRd6Y \\
  --client-id 4vm99luo2k1s9qdvkq9ot4035k \\
  --auth-flow ADMIN_USER_PASSWORD_AUTH \\
  --auth-parameters USERNAME=smoke-test@cyberelites.ae,PASSWORD='Smoke!Test#2026' \\
  --profile forge --region eu-central-1 \\
  --query 'AuthenticationResult.IdToken' --output text`}
          </pre>
        </div>
      </motion.div>
    </div>
  );
}
